import React, { useState, useEffect } from 'react';
import { Card, Typography, Select, Spin, Alert, Space, Button } from 'antd';
import { fetchBiathlonEvents, getNext30Events } from '../services/eqTimingApi';
import { BiathlonEvent } from '../types/api';
import { useBiathlonEvent } from '../contexts/BiathlonEventContext';

const { Title, Text } = Typography;
const { Option } = Select;

const Home: React.FC = () => {
  const [events, setEvents] = useState<BiathlonEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lookbackDays, setLookbackDays] = useState(7);
  const { selectedEvent, setSelectedEvent } = useBiathlonEvent();

  useEffect(() => {
    const loadEvents = async () => {
      try {
        setLoading(true);
        // Store additional lookback days in sessionStorage (resets on page refresh)
        const additionalDays = lookbackDays - 7; // 7 is the base, anything more is additional
        if (additionalDays > 0) {
          sessionStorage.setItem('EVENT_ADDITIONAL_LOOKBACK_DAYS', additionalDays.toString());
        } else {
          sessionStorage.removeItem('EVENT_ADDITIONAL_LOOKBACK_DAYS');
        }
        const allEvents = await fetchBiathlonEvents();
        const next30Events = getNext30Events(allEvents, lookbackDays);
        setEvents(next30Events);
      } catch (err) {
        setError('Failed to load biathlon events');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadEvents();
  }, [lookbackDays]);

  const handleEventSelect = (eventId: number) => {
    const event = events.find(e => e.Id === eventId);
    setSelectedEvent(event || null);
  };

  const formatDate = (date: string) => {
    const eventDate = new Date(date);
    return eventDate.toLocaleDateString('no-NO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="App">
      <header className="App-header">
        <Title level={1}>Welcome to Skytedokumenter</Title>
        <Text style={{ fontSize: '16px', marginBottom: '24px', display: 'block' }}>
          Your cloud document management system
        </Text>
        
        <Card
          title="Select a Biathlon Event"
          style={{
            backgroundColor: 'white',
            margin: '20px',
            maxWidth: 1200,
            width: '100%',
          }}
        >
          {loading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Spin size="large" />
              <Text style={{ display: 'block', marginTop: '10px' }}>Loading biathlon events...</Text>
            </div>
          ) : error ? (
            <Alert message="Error" description={error} type="error" showIcon />
          ) : (
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                  Choose an upcoming biathlon event:
                </Text>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Select
                    placeholder="Select a biathlon event"
                    style={{ width: '100%', maxWidth: '600px' }}
                    size="large"
                    onChange={handleEventSelect}
                    value={selectedEvent?.Id}
                  >
                    {events.map(event => (
                      <Option key={event.Id} value={event.Id}>
                        {event.Name} - {formatDate(event.Date)}
                      </Option>
                    ))}
                  </Select>
                  <Space>
                    <Button
                      type="default"
                      size="large"
                      onClick={() => setLookbackDays(7)}
                      disabled={lookbackDays === 7}
                    >
                      Fetch Newer Events
                    </Button>
                    <Button
                      type="default"
                      size="large"
                      onClick={() => setLookbackDays(prev => prev + 30)}
                    >
                      Fetch Older Events
                    </Button>
                  </Space>
                </Space>
              </div>

              {selectedEvent && (
                <Card
                  title="Selected Event Details"
                  style={{ marginTop: '20px' }}
                  size="small"
                >
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <div>
                      <Text strong style={{ fontSize: '18px' }}>{selectedEvent.Name}</Text>
                    </div>
                    <div>
                      <Text type="secondary">Discipline: </Text>
                      <Text>{selectedEvent.Dicipline.Name}</Text>
                    </div>
                    <div>
                      <Text type="secondary">Date: </Text>
                      <Text>{formatDate(selectedEvent.Date)}</Text>
                    </div>
                    <div>
                      <Text type="secondary">Location: </Text>
                      <Text>{selectedEvent.City.Name}</Text>
                    </div>
                    <div>
                      <Text type="secondary">Organizer: </Text>
                      <Text>{selectedEvent.Organizer.Name}</Text>
                    </div>
                    {selectedEvent.Homepage && (
                      <div>
                        <Text type="secondary">Website: </Text>
                        <a href={selectedEvent.Homepage} target="_blank" rel="noopener noreferrer">
                          {selectedEvent.Homepage}
                        </a>
                      </div>
                    )}
                  </Space>
                </Card>
              )}
            </Space>
          )}
        </Card>
      </header>
    </div>
  );
};

export default Home;
